import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import parse from 'html-react-parser';
import { isJsonString, matchedItems } from '../../Helpers/Calculations';
import { formatCurrency, formatPhone } from '../../Helpers/Formatters';
import colorWheel from '../../Theme/ColorWheel';
import { drugListDisplayNames } from '../controls/GenentechHubPharmacyDictionary';
import check from './check.png';
import empty from './empty.png';
import line from './point.png';

const styles = StyleSheet.create(
    {
        row: { marginTop: 5, marginLeft: 30, fontSize: 10, fontFamily: "RobotoRegular", display: "flex", flexDirection: "row" },
        label: { width: "40%", textAlign: "right", marginRight: 10, color: colorWheel.graniteGrey },
        value: { width: "45%" },
        chkImg: { width: "12px", height: "12px" },
        chkValue: { width: "45%", paddingLeft: "12px" },
        attestCheck: { width: "5%", textAlign: "right", marginRight: 5 },
        attestRight: { width: "90%", display: "flex", flexDirection: "column" },
        attestTitle: { marginBottom: 5 },
        markdownColumn: { marginTop: 5, marginLeft: 30, fontSize: 10, fontFamily: "RobotoRegular", display: "flex", flexDirection: "column" },
        attestText: { fontSize: 8, width: "90%" },
        sig: { marginLeft: 35 },
        chkOpt: { display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' },
        bold: { fontFamily: "RobotoBold" },
        italic: { fontFamily: "RobotoItalic" },
        bolditalic: { fontFamily: "RobotoBoldItalic" },
        htmlcontainer: { marginTop: 5, fontSize: 8, width: "90%" },
        p: { fontSize: 8 },
        ul: { display: "flex", flexDirection: "column", marginBottom: 5 },
        li: { marginLeft: 20, marginTop: 5, fontSize: 8, display: "flex", flexDirection: "row" },
        flexRow: { display: 'flex', flexDirection: 'row', alignContent: 'center', alignItems: 'center' },
        flexColumn: { display: "flex", flexDirection: "column" },
        leftLabel: { width: '30%', textAlign: 'right' },
        rightValue: { width: "75%", textAlign: 'left', paddingLeft: 55 },
        paddingBottom: { paddingBottom: 10 }
    }
);

function htmlDecode(input) { //Special case for > and < in Genentech: Herceptin Product Specific Questions. We should move away from storing &gt; and &lt; in the DB going forward
    var doc = new DOMParser().parseFromString(input, "text/html");
    return doc.documentElement.textContent;
}

const pdfFormat = (value, def) => {
    const { type, sensitive } = def;

    if (!value && isNaN(value)) return "";
    if (sensitive) return "****************";

    if (type === 'date' || type === 'dateWithMaxToday' || type === 'dateWithMaxYesterday') {
        if (value === '') return '';
        var date = new Date(value);
        if (!date) return '';

        return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
    }
    if (type === "currency") return formatCurrency(value, 0);

    if (type === "phone") return formatPhone(value);

    let tempValue = Array.isArray(value) ? value.join(", ") : value;
    let cleanValue = (typeof tempValue === 'string' && tempValue.includes('&')) ? htmlDecode(tempValue) : tempValue;

    return cleanValue;
}

const PdfRow = props => {
    const { def } = props;

    //special cases
    if (def.type === 'amgendrugchecklist') return <PdfAmgenDrugChecklistField {...props} />;
    if (def.type === 'dropdownlist-gooddays-Treatment') return <PdfGoodDaysTreatment {...props} />;
    if (def.type === 'genentechHubPharmacy') return <PdfGenentechHubPharmacy {...props} />;

    //general cases
    if (def.type === 'attest') return <PdfAttestField {...props} />;
    if (def.type === 'checkbox') return <PdfCheckField {...props} />;
    if (def.type === 'signature') return <PdfSignatureField {...props} />;
    if (def.type === 'radio') return <PdfOptionField {...props} />;
    if (def.type === 'radiogrouplist') return <PdfOptionField {...props} />;
    if (def.type === 'checklist') return <PdfOptionField {...props} />;
    if (def.type === 'select') return <PdfOptionField {...props} />;
    if (def.type === 'markdown') return <PdfMarkdownField {...props} />;
    if (def.type === 'html') return <PdfHtmlField {...props} />;

    return <PdfGeneralField {...props} />;
}

/*************************
 * Special Cases
 ************************/
const PdfAmgenDrugChecklistField = props => {
    const { def, data } = props;

    //A2-1612: Need to treat Neulasta Onpro Kit as though it were Neulasta
    var dataCopy = data;
    var onproIndex = dataCopy.findIndex(d => d === 'Neulasta Onpro Kit');
    if (onproIndex !== -1) dataCopy[onproIndex] = 'Neulasta';

    const options = matchedItems(dataCopy, def.values, def.options);

    return <View style={styles.row}>
        <Text style={styles.label}>{def.label}</Text>
        <View>
            {options && options.map(opt =>
                <View style={styles.chkOpt}>
                    <Image style={styles.chkImg} src={check} />
                    <Text style={styles.chkValue}>{opt}</Text>
                </View>
            )}
        </View>
    </View>
}

const PdfGoodDaysTreatment = props => {
    const { def, data } = props;

    var displayValue = isJsonString(data) ? JSON.parse(data).name.toLowerCase() : data.toLowerCase();
    displayValue = displayValue.charAt(0).toUpperCase() + displayValue.slice(1);

    return <View style={styles.row}>
        <Text style={styles.label}>{def.label.trim()}</Text>
        <Text style={styles.value}>{displayValue}</Text>
    </View>
}

const PdfGenentechHubPharmacy = props => {
    const { def, data } = props;
    const drugList = JSON.parse(data);
    return <View style={styles.row}>
        <Text style={styles.label}>{def.label.trim()}</Text>
        <View style={styles.flexColumn}>
            {drugList && drugList.map((drug, i) => {
                return (
                    <View key={drug.drugName + i} style={styles.paddingBottom}>
                        <Text style={styles.label}>{`Prescription for ${drugListDisplayNames[drug.drugName]}`}</Text>
                        <View style={{ marginLeft: -125 }}>
                            <LabelValueRow label='Shipment Option' value={drug.shipmentOption} />
                            {drug.prescriptionType !== '' && <LabelValueRow label='Prescription Type' value={drug.prescriptionType} />}
                            {drug.prescriptionOption !== '' && <LongLabelValueRow label='Prescription Option' value={drug.prescriptionOption} />}
                            {drug.numOfTablets !== '' && <LabelValueRow label='Number of Tablets' value={drug.numOfTablets} />}
                            {drug.dosages.length > 0 && drug.dosages.map((d, i) =>
                                <LabelValueRow label='Dosage' value={`${d.Dosage} ${d.DosageUnit}`} key={d.Dosage + d.DosageUnit + i} />
                            )}
                            {drug.sig !== '' && <LabelValueRow label='SIG' value={drug.sig} />}
                            {drug.frequencyOfAdministration !== '' && <LabelValueRow label='Frequency of Administration' value={drug.frequencyOfAdministration} />}
                            {drug.refills !== '' && <LabelValueRow label='Refill(s)' value={drug.refills} />}
                            {drug.dispenseDetails.length > 0 && drug.dispenseDetails.map((d, i) =>
                                <LabelValueRow label='Dispense' value={`${d.DispenseQuantity} ${d.DispenseUnitOptions}`} key={d.DispenseQuantity + d.DispenseUnitOptions + i} />
                            )}
                            {drug.daysOff !== '' && <LabelValueRow label='Days Off' value={drug.daysOff} />}
                            {drug.numOfTabletsPerCycle !== '' && <LabelValueRow label='Number of Tablets Per Cycle' value={drug.numOfTabletsPerCycle} />}
                            {drug.numOfCyclesPerFill !== '' && <LabelValueRow label='Number of Cycles Per Fill' value={drug.numOfCyclesPerFill} />}
                        </View>
                    </View>
                )
            })}
        </View>
    </View>
}

/*************************
 * General Cases
 ************************/
const PdfGeneralField = props => {
    const { def, data } = props;
    const myData = pdfFormat(data, def);

    return <View style={styles.row}>
        <Text style={styles.label}>{def.label.trim()}</Text>
        <Text style={styles.value}>{myData}</Text>
    </View>
}

const PdfOptionField = props => {
    const { def, data } = props;

    if (def.options.length === 1 && def.options[0] === '') {
        //this is the special case where we are mimicking an attestation with
        //a single-option checklist so the user sees LABEL []
        return <View style={styles.row} wrap={false}>
            <Text style={styles.label}>{def.label.trim()}</Text>
            <Text style={styles.value}>{data}</Text>
        </View>
    }

    const options = matchedItems(data, def.values, def.options);
    const text = pdfFormat(options, def);

    return <View style={styles.row} wrap={false}>
        <Text style={styles.label}>{def.label.trim()}</Text>
        <Text style={styles.value}>{text}</Text>
    </View>
}

const PdfMarkdownField = props => {
    const { def } = props;

    //react-pdf/renderer cant handle the tab character, so I'm replacing it with html spaces  (would be nice if it had whiteSpace: "pre-wrap" as a supported prop)
    //this is super-clunky, we are ripping out bold/italic from the markdown because we cannot parse it properly YET.
    const replaceTabs = def.markdown
        ? htmlDecode(
            def.markdown
                .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;')
                .replaceAll("\\*", "")
                .replaceAll("#####", "")
                .replaceAll("####", "")
                .replaceAll("###", "")
                .replaceAll("##", "")
                .replaceAll("**", "")
                .replaceAll("*", "")
                .replaceAll("<u>", "")
                .replaceAll("</u>", "")
                .replaceAll("_", ""))
        : null;

    if (def.label) {
        return <View style={styles.markdownColumn}>
            <Text style={styles.attestTitle}>{def.label.trim()}</Text>
            <Text style={styles.attestText}>{replaceTabs}</Text>
        </View >
    }
    else {
        return <View style={styles.row} >
            <Text style={styles.attestText}>{replaceTabs}</Text>
        </View >
    }

}

const PdfHtmlField = props => {
    const { def } = props;

    const html = def.markdown.replace(/\r?\n|\r/g, ' ');
    const parsed = parse(html);

    const elements = parsed.map((ele, index) => renderElement(ele, index));

    return <View style={styles.htmlcontainer}>
        {elements}
    </View>
}

const renderElement = (ele, index) => {
    if (!ele.props) return <Text>{ele}</Text>;

    const children = ele.props.children;

    switch (ele.type) {
        case "p":
            const eleChildren = children.map(child => {
                return renderElement(child)
            });
            return <View style={styles.p} key={index}>{eleChildren}</View>;

        //super naive handling of bold and italic case
        case "b":
            if (children[0].type === "i") return <Text style={styles.bolditalic} key={index}>{children[0].props.children}</Text>;

            return <Text style={styles.bold} key={index}>{children}</Text>;

        case "i":
            if (children[0].type === "b") return <Text style={styles.bolditalic} key={index}>{children[0].props.children}</Text>;

            return <Text style={styles.italic} key={index}>{children}</Text>;

        case "ul":
            const ulChildren = children.map(child => {
                return renderElement(child);
            });

            return <View style={styles.ul} key={index}>{ulChildren}</View>

        case "li":
            return <View style={styles.li} key={index}>
                <Text style={{ marginRight: 5 }} key={`bullet_${index}`}>&bull;</Text>
                <Text key={`text_${index}`}>{children}</Text>
            </View>
        default:
            return null;
    }
}

const PdfCheckField = props => {
    const { def, data } = props;
    const src = data
        ? check
        : empty;

    return <View style={styles.row}>
        <Text style={styles.label}>{def.label.trim()}</Text>
        <View style={styles.attestCheck}>
            <Image style={styles.chkImg} src={src} />
        </View>
    </View>
}

const PdfAttestField = props => {
    const { def, data } = props;
    const src = data
        ? check
        : empty;

    //react-pdf/renderer cant handle the tab character, so I'm replacing it with html spaces  (would be nice if it had whiteSpace: "pre-wrap" as a supported prop)
    //this is super-clunky, we are ripping out bold/italic from the markdown because we cannot parse it properly YET.
    const replaceTabs = def.markdown
        ? htmlDecode(
            def.markdown
                .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;')
                .replaceAll("\\*", "")
                .replaceAll("#####", "")
                .replaceAll("####", "")
                .replaceAll("###", "")
                .replaceAll("##", "")
                .replaceAll("**", "")
                .replaceAll("*", "")
                .replaceAll("<u>", "")
                .replaceAll("</u>", "")
                .replaceAll("_", ""))
        : null;

    return <View style={styles.row}>
        <View style={styles.attestCheck}>
            <Image style={styles.chkImg} src={src} />
        </View>

        <View style={styles.attestRight}>
            <Text style={styles.attestTitle}>{def.label.trim()}</Text>
            <Text style={styles.attestText}>{replaceTabs}</Text>
        </View>
    </View>
}

const PdfSignatureField = props => {
    const { def, data } = props;

    if (!data) return null;

    const src = { uri: data };

    return <View style={styles.row} wrap={false}>
        <Text style={styles.label}>{def.label.trim()}</Text>
        <View style={styles.sig}>
            {/* The border style didn't work. How do I make a box around the sig? */}
            <Image src={src} style={{ width: 200, height: 25, border: "1px solid black" }} />
            <Image src={line} style={{ width: 200, height: "1px" }} />
        </View>
    </View>
}

const LabelValueRow = props => {
    const { label, value } = props;

    return <View style={styles.flexRow}>
        <Text style={styles.leftLabel}>{label}</Text>
        <Text style={styles.rightValue}>{value}</Text>
    </View>
}

const LongLabelValueRow = props => {
    const { label, value } = props;

    const style = { ...styles.rightValue, fontSize: 8 }
    //'1200 mg pertuzumab, 600 mg trastuzumab and 30,000 units hyaluronidase'
    var length = 0;
    if (value && value.length) length = value.length;
    return (length < 51)
        ? <LabelValueRow label={label} value={value} />
        : <View style={styles.flexRow}>
            <Text style={styles.leftLabel}>{label}</Text>
            <Text style={style}>{value}</Text>
        </View>
}



export default PdfRow;
